import { CommonModule } from '@angular/common';
import { Component, input, signal, ViewChild } from '@angular/core';
import {
  ActionBarRight,
  AwActionBarComponent,
  AwDividerComponent,
  AwSpinnerComponent,
  AwToastComponent,
  BreadCrumb,
  ToastVariants,
} from '@assetworks-llc/aw-component-lib';
import { SingleSelectOption } from '@assetworks-llc/aw-component-lib';
import { AwBreadCrumbComponent } from '@assetworks-llc/aw-component-lib';
import { AwTextFormComponent } from '../../../../../shared/components/aw-text-form/aw-text-form.component';
import { AwDropdownFormComponent } from '../../../../../shared/components/aw-dropdown-form/aw-dropdown-form.component';
import { Router } from '@angular/router';
import { of, switchMap } from 'rxjs';
import { AwAuthFormComponent } from '../../../components/aw-auth-form/aw-auth-form.component';
import {
  AuthInputValues,
  AuthTypeValues,
  DEFAULT_AUTH_VALUES,
} from '../../../../../types/types';
import { ParentIntegrationService } from '../../../../../core/services/parent-integration.service';
import { EmployeeAuthenticationService } from '../../../../../core/services/employee-authentication.service';
import { CreateSecretRequest } from '../../../../../api/models';
import { SecretService } from '../../../../../core/services/secret.service';

@Component({
  selector: 'app-add-parent-integration',
  imports: [
    AwDividerComponent,
    AwBreadCrumbComponent,
    AwTextFormComponent,
    CommonModule,
    AwSpinnerComponent,
    AwActionBarComponent,
    AwDropdownFormComponent,
    AwToastComponent,
    AwAuthFormComponent,
  ],
  templateUrl: './add-parent-integration.component.html',
  styleUrls: [
    './add-parent-integration.component.scss',
    '../../../../../../styles/management.scss',
  ],
  standalone: true,
})
export class AddParentIntegrationComponent {
  constructor(
    private router: Router,
    private parentIntegrationService: ParentIntegrationService,
    private employeeAuthenticationService: EmployeeAuthenticationService,
    private secretService: SecretService
  ) {}

  @ViewChild(AwToastComponent) toastComponent!: AwToastComponent;

  isLoading: boolean = false;

  nameInput: string = '';
  statusInput: string = '';
  statusValue!: boolean;

  employeeAuthTypeValue: AuthTypeValues = AuthTypeValues.OPENIDCONNECT;
  employeeAuthFieldsValues: AuthInputValues = DEFAULT_AUTH_VALUES;
  employeeAuthFieldsFilled: boolean = false;

  breadcrumbs = signal<BreadCrumb[]>([
    {
      label: 'Parent Integration Management',
      link: '/parent-integration-management/overview',
      newTab: false,
    },
    {
      label: 'New Parent Integration',
      link: '/parent-integration-management/add-parent-integration',
      newTab: false,
    },
  ]);

  statusOptions = signal<SingleSelectOption[]>([
    { label: 'Active' },
    { label: 'Disabled' },
  ]);

  actionsRight = input<ActionBarRight[]>([
    {
      buttonCallback: {
        label: 'Cancel',
        buttonType: 'primary',
        action: () => this.router.navigate(['/parent-integration-management/overview']),
      },
    },
    {
      buttonCallback: {
        label: 'Save',
        buttonType: 'outlined',
        action: () => this.createParentIntegration(),
      },
    },
  ]);

  onNameSelected(text: string | null) {
    this.nameInput = text || '';
  }

  onStatusSelected(status: SingleSelectOption | null) {
    this.statusInput = status?.label || '';
    if (status?.label === 'Active') {
      this.statusValue = true;
    } else if (status?.label === 'Disabled') {
      this.statusValue = false;
    }
  }

  onEmployeeAuthValuesChanged(event: {
    values: AuthInputValues;
    fieldsFilled: boolean;
    someFieldsFilled: boolean;
  }) {
    this.employeeAuthFieldsFilled = event.fieldsFilled;
    this.employeeAuthFieldsValues = event.values;
  }

  buildToastMessage(message: string, variant: ToastVariants): void {
    this.toastComponent.showToast({
      variant: variant,
      title: message,
      description: '',
      removeIcon: true,
      removeX: false,
      alwaysShow: false,
      toastPositionTopLeft: false,
      multiToasts: false,
    });
  }

  validateFields(): boolean {
    return !!(
      this.nameInput &&
      this.statusValue !== null &&
      this.statusValue !== undefined
    );
  }

  createParentIntegration(): void {
    if (!this.validateFields()) {
      this.buildToastMessage('Fill in all required fields', 'error');
      return;
    }

    if (!this.employeeAuthFieldsFilled) {
      this.buildToastMessage(
        'Fill in all employee authentication fields',
        'error'
      );
      return;
    }

    this.isLoading = true;

    const secretName = 'employeeOpenIdClientSecret';

    this.parentIntegrationService
      .createParentIntegration({
        body: {
          name: this.nameInput,
          enabled: this.statusValue,
        },
      })
      .pipe(
        switchMap((response) => {
          const organizationId = response.organizationId!;

          const secretInfo: CreateSecretRequest = {
            secretName: secretName,
            secretValue: this.employeeAuthFieldsValues.client_secret ?? '',
          };

          return this.secretService
            .createSecret({
              organizationId: organizationId,
              body: secretInfo,
            })
            .pipe(
              switchMap(() =>
                this.employeeAuthenticationService.createAuthenticationConfig({
                  organizationId: organizationId,
                  authType: this.employeeAuthTypeValue,
                  authConfigValues: this.employeeAuthFieldsValues,
                  secretName: secretName,
                })
              )
            );
        })
      )
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.parentIntegrationService.clearCache();
          this.buildToastMessage(
            'Successfully created parent integration',
            'success'
          );
          this.router.navigate(['/parent-integration-management/overview']);
        },
        error: () => {
          this.isLoading = false;
          this.buildToastMessage(
            'Failed to create parent integration',
            'error'
          );
        },
      });
  }
}
